<template>
  <div class="research-tab" :class="{ 'media-mode-active': layoutMode === 'media' }">
    <!-- Header -->
    <div class="research-header">
      <div>
        <h1>Research</h1>
        <p class="sub">Companies, web + Instagram snapshots (Apify)</p>
      </div>
      <n-space align="center">
        <n-radio-group v-model:value="layoutMode" name="layout-mode" size="small">
          <n-radio-button value="list">List</n-radio-button>
          <n-radio-button value="media">Media</n-radio-button>
        </n-radio-group>
        <n-button type="primary" @click="showCreate = true">Add company</n-button>
      </n-space>
    </div>

    <!-- ── LIST MODE ── -->
    <template v-if="layoutMode === 'list'">
      <div class="list-toolbar">
        <n-input
          v-model:value="companySearch"
          placeholder="Search companies…"
          clearable
          style="width: 280px"
        />
        <n-select
          v-model:value="companySort"
          :options="sortOptions"
          style="width: 180px"
        />
      </div>

      <div class="list-mode-body">
        <div class="list-main" :class="{ 'with-panel': previewPanelOpen && detailId }">
          <n-spin :show="loadingList">
            <n-data-table
              :columns="listModeColumns"
              :data="filteredCompanies"
              :row-key="(r: CompanyRow) => r.id"
              :row-props="(r: CompanyRow) => ({ onClick: () => selectCompany(r.id), class: detailId === r.id ? 'row-selected' : '' })"
              :pagination="false"
            />
          </n-spin>
        </div>

        <!-- Right preview panel -->
        <transition name="slide-right">
          <div v-if="previewPanelOpen && detailId" class="preview-panel">
            <div class="preview-panel-header">
              <span class="preview-company-name">{{ detailCompany?.name || '…' }}</span>
              <n-button quaternary size="tiny" @click="previewPanelOpen = false">✕</n-button>
            </div>

            <n-spin :show="loadingDetail" size="small">
              <div v-if="detailCompany">
                <!-- Stats row -->
                <div class="preview-stats">
                  <div class="stat-item">
                    <div class="stat-val">{{ igSummary.followersLabel }}</div>
                    <div class="stat-lbl">Followers</div>
                  </div>
                  <div class="stat-item">
                    <div class="stat-val">{{ igSummary.postsLabel }}</div>
                    <div class="stat-lbl">Posts</div>
                  </div>
                  <div class="stat-item">
                    <div class="stat-val">{{ igSummary.avgLikesLabel }}</div>
                    <div class="stat-lbl">Avg ♥</div>
                  </div>
                </div>

                <!-- Thumbnail mini-grid -->
                <div class="preview-thumb-grid">
                  <img
                    v-for="(url, i) in previewThumbs"
                    :key="i"
                    :src="url"
                    class="preview-thumb"
                    loading="lazy"
                  />
                  <div v-if="!previewThumbs.length" class="preview-no-media">No media yet</div>
                </div>

                <!-- Jump to media mode -->
                <n-button block size="small" style="margin-top: 12px" @click="layoutMode = 'media'">
                  View media →
                </n-button>
              </div>
              <div v-else class="preview-loading">Loading…</div>
            </n-spin>
          </div>
        </transition>

        <!-- Reopen panel trigger -->
        <n-button
          v-if="!previewPanelOpen && detailId"
          quaternary
          size="small"
          class="panel-reopen-btn"
          @click="previewPanelOpen = true"
        >
          ◀ Preview
        </n-button>
      </div>
    </template>

    <!-- ── MEDIA MODE ── -->
    <template v-else>
      <div class="media-mode-layout">
        <!-- Left sidebar: compact company list -->
        <div class="media-sidebar">
          <div class="sidebar-search">
            <n-input
              v-model:value="companySearch"
              placeholder="Filter…"
              clearable
              size="small"
            />
          </div>
          <div class="sidebar-list">
            <div
              v-for="c in filteredCompanies"
              :key="c.id"
              class="sidebar-item"
              :class="{ active: detailId === c.id }"
              @click="selectCompany(c.id)"
            >
              <span class="sidebar-name">{{ c.name }}</span>
              <span v-if="c.relationType" class="sidebar-relation">{{ c.relationType }}</span>
              <span
                class="status-dot"
                :class="c.researchInProgress ? 'dot-running' : 'dot-idle'"
                :title="c.researchInProgress ? 'Running' : 'Idle'"
              />
            </div>
          </div>
        </div>

        <!-- Main content: inline company detail -->
        <div class="media-main">
          <n-spin v-if="loadingDetail" style="padding: 40px; display: flex; justify-content: center;" />

          <template v-else-if="detailCompany">
            <!-- Compact company header row -->
            <div class="media-company-header">
              <div class="media-company-info">
                <span class="media-company-name">{{ detailCompany.name }}</span>
                <a
                  v-if="detailCompany.websiteUrl"
                  :href="detailCompany.websiteUrl as string"
                  target="_blank"
                  rel="noopener"
                  class="media-company-link"
                >{{ detailCompany.websiteUrl }}</a>
                <span class="media-company-ig">@{{ detailCompany.instagramHandle }}</span>
              </div>
              <n-space align="center" size="small">
                <n-tag v-if="detailCompany.researchInProgress" type="info" size="small">Running…</n-tag>
                <n-tag v-if="detailCompany.lastResearchError" type="warning" size="small">Error</n-tag>
                <n-radio-group v-model:value="runDepth" name="depth-media" size="small">
                  <n-radio-button value="light">Light</n-radio-button>
                  <n-radio-button value="default">Default</n-radio-button>
                  <n-radio-button value="heavy">Heavy</n-radio-button>
                </n-radio-group>
                <n-button
                  size="small"
                  type="primary"
                  :loading="startingRun"
                  :disabled="!!detailCompany.researchInProgress"
                  @click="startRun"
                >
                  Run
                </n-button>
                <n-button
                  v-if="detailCompany.researchInProgress"
                  size="small"
                  type="warning"
                  secondary
                  :loading="cancellingRun"
                  @click="cancelRun"
                >
                  Cancel
                </n-button>
              </n-space>
            </div>

            <!-- Research session tabs -->
            <template v-if="sessionsList.length">
              <n-tabs
                type="line"
                :value="activeSessionId ?? undefined"
                @update:value="onSessionTabChange"
              >
                <n-tab-pane
                  v-for="s in sessionsList"
                  :key="s.id"
                  :name="s.id"
                  :tab="sessionTabTitle(s)"
                >
                  <n-tabs v-model:value="innerTab" type="segment" size="small" class="inner-tabs">

                    <!-- POSTS: redesigned card grid -->
                    <n-tab-pane name="posts" tab="Posts">
                      <div v-if="enrichedPosts.length" class="post-card-grid">
                        <div
                          v-for="p in enrichedPosts"
                          :key="p.id as string"
                          class="post-media-card"
                          @click="openPostModal(p)"
                        >
                          <div class="post-thumb-wrap">
                            <img
                              v-if="p._thumb"
                              :src="p._thumb"
                              alt=""
                              class="post-thumb"
                              loading="lazy"
                            />
                            <div v-else class="post-thumb-placeholder" />
                          </div>
                          <div class="post-card-body">
                            <p class="post-caption-clip">{{ p.caption || '' }}</p>
                            <div class="post-card-meta">
                              <span>♥ {{ p.likeCount ?? '—' }}</span>
                              <span>💬 {{ p.commentCount ?? '—' }}</span>
                              <span class="post-date">{{ formatDate(p.postedAt) }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- Fallback: live DB posts if no export posts -->
                      <div v-else-if="detailPosts.length" class="post-card-grid">
                        <div
                          v-for="p in detailPosts"
                          :key="p.id as string"
                          class="post-media-card"
                          @click="openPostModal(p)"
                        >
                          <div class="post-thumb-wrap">
                            <img
                              v-if="liveThumb(p)"
                              :src="liveThumb(p)!"
                              alt=""
                              class="post-thumb"
                              loading="lazy"
                            />
                            <div v-else class="post-thumb-placeholder" />
                          </div>
                          <div class="post-card-body">
                            <p class="post-caption-clip">{{ p.caption || '' }}</p>
                            <div class="post-card-meta">
                              <span>♥ {{ p.likeCount ?? '—' }}</span>
                              <span>💬 {{ p.commentCount ?? '—' }}</span>
                              <span class="post-date">{{ formatDate(p.postedAt) }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <n-empty v-else description="No posts in this export" />
                    </n-tab-pane>

                    <!-- WEB -->
                    <n-tab-pane name="web" tab="Web">
                      <n-list v-if="exportWeb?.results?.length" bordered>
                        <n-list-item v-for="(r, i) in exportWeb.results" :key="i">
                          <div>
                            <a :href="r.url" target="_blank" rel="noopener">{{ r.title || r.url }}</a>
                            <div class="snippet">{{ r.snippet }}</div>
                          </div>
                        </n-list-item>
                      </n-list>
                      <n-empty v-else description="No web results in export" />
                    </n-tab-pane>

                    <!-- SITE -->
                    <n-tab-pane name="website" tab="Site">
                      <template v-if="exportWebsite">
                        <n-space vertical size="small">
                          <n-card size="small" title="Cached website media">
                            <div v-if="websiteThumbs.length" class="site-media-grid">
                              <img
                                v-for="(u, i) in websiteThumbs"
                                :key="`site-thumb-${i}`"
                                :src="u"
                                alt=""
                                class="site-thumb"
                                loading="lazy"
                              />
                            </div>
                            <n-empty v-else description="No cached site images yet" />
                          </n-card>
                          <pre class="json-pre">{{ pretty(exportWebsite) }}</pre>
                        </n-space>
                      </template>
                      <n-empty v-else description="No website snapshot in export" />
                    </n-tab-pane>

                    <!-- IG ACCOUNT -->
                    <n-tab-pane name="instagram" tab="IG account">
                      <template v-if="exportIgAccount">
                        <n-card size="small" title="Account summary" style="margin-bottom: 12px">
                          <n-descriptions :column="2" size="small" bordered>
                            <n-descriptions-item label="Username">{{ igSummary.username || '—' }}</n-descriptions-item>
                            <n-descriptions-item label="Display name">{{ igSummary.fullName || '—' }}</n-descriptions-item>
                            <n-descriptions-item label="Business category">{{ igSummary.businessCategory || '—' }}</n-descriptions-item>
                            <n-descriptions-item label="Business account">{{ igSummary.isBusiness ? 'Yes' : 'No' }}</n-descriptions-item>
                            <n-descriptions-item label="Followers">{{ igSummary.followersLabel }}</n-descriptions-item>
                            <n-descriptions-item label="Following">{{ igSummary.followingLabel }}</n-descriptions-item>
                            <n-descriptions-item label="Posts">{{ igSummary.postsLabel }}</n-descriptions-item>
                            <n-descriptions-item label="Engagement (avg/post)">{{ igSummary.avgEngagementLabel }}</n-descriptions-item>
                            <n-descriptions-item label="Avg likes">{{ igSummary.avgLikesLabel }}</n-descriptions-item>
                            <n-descriptions-item label="Avg comments">{{ igSummary.avgCommentsLabel }}</n-descriptions-item>
                            <n-descriptions-item label="Top liked post">{{ igSummary.topLikedLabel }}</n-descriptions-item>
                            <n-descriptions-item label="Top commented post">{{ igSummary.topCommentedLabel }}</n-descriptions-item>
                          </n-descriptions>
                        </n-card>
                        <pre class="json-pre">{{ pretty(exportIgAccount) }}</pre>
                      </template>
                      <n-empty v-else description="No IG account in export" />
                    </n-tab-pane>

                    <!-- PHASES -->
                    <n-tab-pane name="runs" tab="Phases">
                      <n-data-table
                        :columns="runColumns"
                        :data="sessionRuns"
                        :pagination="false"
                        size="small"
                      />
                    </n-tab-pane>

                  </n-tabs>
                </n-tab-pane>
              </n-tabs>
            </template>
            <n-empty v-else description="Run research to capture a dated session export" />
          </template>

          <div v-else class="media-empty-state">
            <n-empty description="Select a company from the sidebar" />
          </div>
        </div>
      </div>
    </template>

    <!-- ── CREATE MODAL ── -->
    <n-modal v-model:show="showCreate" preset="card" title="New company" style="width: 520px">
      <n-form label-placement="top">
        <n-form-item label="Name">
          <n-input v-model:value="createForm.name" placeholder="Business name" />
        </n-form-item>
        <n-form-item label="Website URL">
          <n-input v-model:value="createForm.websiteUrl" placeholder="https://example.com" />
        </n-form-item>
        <n-form-item label="Instagram handle">
          <n-input v-model:value="createForm.instagramHandle" placeholder="@brand or brand" />
        </n-form-item>
        <n-form-item label="Relation (optional)">
          <n-select
            v-model:value="createForm.relationType"
            :options="relationOptions"
            clearable
            placeholder="—"
          />
        </n-form-item>
      </n-form>
      <template #footer>
        <n-space justify="end">
          <n-button @click="showCreate = false">Cancel</n-button>
          <n-button type="primary" :loading="creating" @click="submitCreate">Create</n-button>
        </n-space>
      </template>
    </n-modal>

    <!-- ── POST DETAIL MODAL ── -->
    <n-modal
      v-model:show="postModalOpen"
      :mask-closable="true"
      style="width: 85vw; max-width: 1400px"
    >
      <n-card
        v-if="selectedPost"
        :title="selectedPost.permalink ? 'Post' : 'Post detail'"
        closable
        style="height: 88vh; display: flex; flex-direction: column;"
        @close="postModalOpen = false"
      >
        <div class="post-modal-body">
          <!-- Left: media -->
          <div class="post-modal-media">
            <template v-if="modalMediaUrls.length > 1">
              <div class="carousel-wrap">
                <n-button quaternary :disabled="postCarouselIndex === 0" @click="prevSlide">◀</n-button>
                <img :src="modalMediaUrls[postCarouselIndex]" class="modal-media-img" alt="" />
                <n-button quaternary :disabled="postCarouselIndex === modalMediaUrls.length - 1" @click="nextSlide">▶</n-button>
              </div>
              <div class="carousel-dots">
                <span
                  v-for="(_, i) in modalMediaUrls"
                  :key="i"
                  :class="['carousel-dot', { active: i === postCarouselIndex }]"
                  @click="postCarouselIndex = i"
                />
              </div>
            </template>
            <template v-else-if="modalMediaUrls.length === 1">
              <img :src="modalMediaUrls[0]" class="modal-media-img" alt="" />
            </template>
            <template v-else>
              <div class="modal-media-placeholder">
                <span style="font-size: 32px">▶</span>
                <span>Video / Reel</span>
                <a
                  v-if="selectedPost.permalink"
                  :href="selectedPost.permalink as string"
                  target="_blank"
                  rel="noopener"
                  style="color: #fff; text-decoration: underline; margin-top: 8px"
                >
                  View on Instagram ↗
                </a>
              </div>
            </template>
          </div>

          <!-- Right: post info -->
          <div class="post-modal-info">
            <div class="post-modal-meta">
              <span>♥ {{ selectedPost.likeCount ?? '—' }}</span>
              <span>💬 {{ selectedPost.commentCount ?? '—' }}</span>
              <span>{{ formatDate(selectedPost.postedAt) }}</span>
              <a
                v-if="selectedPost.permalink"
                :href="selectedPost.permalink as string"
                target="_blank"
                rel="noopener"
                style="margin-left: auto; font-size: 12px"
              >
                Open on Instagram ↗
              </a>
            </div>

            <div class="post-modal-caption">{{ selectedPost.caption || '' }}</div>

            <template v-if="(selectedPost as any)._comments?.length">
              <div class="post-modal-comments-heading">
                Comments ({{ (selectedPost as any)._comments.length }})
              </div>
              <div class="post-modal-comments">
                <pre
                  v-for="(c, i) in (selectedPost as any)._comments"
                  :key="i"
                  class="json-pre sm"
                >{{ pretty(c) }}</pre>
              </div>
            </template>
          </div>
        </div>
      </n-card>
    </n-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, h } from 'vue'
import { NButton, NDataTable, useMessage, type DataTableColumns } from 'naive-ui'
import { apiClient } from '@/services/api'

interface CompanyRow {
  id: string
  name: string
  websiteUrl: string
  instagramHandle: string
  relationType: string | null
  researchInProgress: boolean
  lastResearchAt: string | null
  lastResearchError: string | null
}

interface SessionRow {
  id: string
  startedAt: string
  finishedAt: string | null
  depth: string | null
}

const message = useMessage()
const loadingList = ref(false)
const loadingDetail = ref(false)
const companies = ref<CompanyRow[]>([])
const showCreate = ref(false)
const creating = ref(false)
const createForm = ref({
  name: '',
  websiteUrl: '',
  instagramHandle: '',
  relationType: null as null | 'client' | 'prospect' | 'competitor' | 'reference',
})
const relationOptions = [
  { label: 'Client', value: 'client' },
  { label: 'Prospect', value: 'prospect' },
  { label: 'Competitor', value: 'competitor' },
  { label: 'Reference', value: 'reference' },
]

// Layout mode
type LayoutMode = 'list' | 'media'
const layoutMode = ref<LayoutMode>('list')
const previewPanelOpen = ref(true)
const companySearch = ref('')
const companySort = ref<'name_asc' | 'name_desc' | 'recent' | 'relation'>('name_asc')
const sortOptions = [
  { label: 'Name A→Z', value: 'name_asc' },
  { label: 'Name Z→A', value: 'name_desc' },
  { label: 'Recent research', value: 'recent' },
  { label: 'Relation type', value: 'relation' },
]

// Post modal
const postModalOpen = ref(false)
const selectedPost = ref<Record<string, unknown> | null>(null)
const postCarouselIndex = ref(0)

const detailId = ref<string | null>(null)
const detailCompany = ref<Record<string, any> | null>(null)
const detailPosts = ref<Array<Record<string, unknown>>>([])
const runDepth = ref<'light' | 'default' | 'heavy'>('default')
const startingRun = ref(false)
const cancellingRun = ref(false)
const deletingCompanyId = ref<string | null>(null)

const sessionsList = ref<SessionRow[]>([])
const activeSessionId = ref<string | null>(null)
const sessionExport = ref<Record<string, any> | null>(null)
const sessionRuns = ref<
  Array<{
    id: string
    source: string
    status: string
    depth: string | null
    apifyRunId: string | null
    error: string | null
    createdAt: string
  }>
>([])
const innerTab = ref('posts')

let pollTimer: ReturnType<typeof setInterval> | null = null

// ── Computed ──

const filteredCompanies = computed(() => {
  let list = companies.value
  const q = companySearch.value.trim().toLowerCase()
  if (q) {
    list = list.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.instagramHandle.toLowerCase().includes(q),
    )
  }
  const sorted = [...list]
  switch (companySort.value) {
    case 'name_desc':
      sorted.sort((a, b) => b.name.localeCompare(a.name))
      break
    case 'recent':
      sorted.sort((a, b) => {
        if (!a.lastResearchAt) return 1
        if (!b.lastResearchAt) return -1
        return new Date(b.lastResearchAt).getTime() - new Date(a.lastResearchAt).getTime()
      })
      break
    case 'relation':
      sorted.sort((a, b) => (a.relationType ?? 'z').localeCompare(b.relationType ?? 'z'))
      break
    default:
      sorted.sort((a, b) => a.name.localeCompare(b.name))
  }
  return sorted
})

const exportWeb = computed(() => sessionExport.value?.web as { results?: Array<{ title: string; url: string; snippet: string }> } | undefined)
const exportWebsite = computed(() => sessionExport.value?.website)
const exportIgAccount = computed(() => sessionExport.value?.instagram?.account)
const exportPosts = computed(() => {
  const ig = sessionExport.value?.instagram as { posts?: Array<Record<string, unknown>> } | undefined
  return ig?.posts ?? []
})
const websiteThumbs = computed(() => {
  const site = (exportWebsite.value ?? {}) as Record<string, unknown>
  const cachedPhotos = Array.isArray(site.cachedPhotoThumbUrls) ? (site.cachedPhotoThumbUrls as string[]) : []
  const cachedLogos = Array.isArray(site.cachedLogoThumbUrls) ? (site.cachedLogoThumbUrls as string[]) : []
  const fallbackPhotos = Array.isArray(site.photoUrls) ? (site.photoUrls as string[]) : []
  const fallbackLogos = Array.isArray(site.logoUrls) ? (site.logoUrls as string[]) : []
  const combined = [...cachedPhotos, ...cachedLogos]
  const fallbackCombined = [...fallbackPhotos, ...fallbackLogos]
  return Array.from(new Set((combined.length ? combined : fallbackCombined).filter(Boolean))).slice(0, 24)
})

const igSummary = computed(() => {
  const account = (exportIgAccount.value ?? {}) as Record<string, unknown>
  const posts = exportPosts.value

  const asNumber = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v)
      ? v
      : typeof v === 'string' && v.trim() && !Number.isNaN(Number(v))
        ? Number(v)
        : null

  const followers = asNumber(account.followersCount)
  const following = asNumber(account.followsCount)
  const declaredPosts = asNumber(account.postsCount)
  const postsCount = declaredPosts ?? posts.length

  const likes = posts.map((p) => asNumber(p.likeCount)).filter((n): n is number => n !== null)
  const comments = posts.map((p) => asNumber(p.commentCount)).filter((n): n is number => n !== null)
  const likeSum = likes.reduce((a, b) => a + b, 0)
  const commentSum = comments.reduce((a, b) => a + b, 0)
  const avgLikes = likes.length ? likeSum / likes.length : null
  const avgComments = comments.length ? commentSum / comments.length : null
  const avgEngagement = avgLikes !== null || avgComments !== null ? (avgLikes ?? 0) + (avgComments ?? 0) : null

  const ranked = posts
    .map((p) => ({
      permalink: typeof p.permalink === 'string' ? p.permalink : null,
      likeCount: asNumber(p.likeCount) ?? 0,
      commentCount: asNumber(p.commentCount) ?? 0,
    }))
    .filter((p) => p.permalink)
  const topLiked = ranked.length
    ? ranked.reduce((best, cur) => (cur.likeCount > best.likeCount ? cur : best))
    : null
  const topCommented = ranked.length
    ? ranked.reduce((best, cur) => (cur.commentCount > best.commentCount ? cur : best))
    : null

  const fmt = (n: number | null): string => (n === null ? '—' : Intl.NumberFormat().format(Math.round(n)))

  return {
    username: typeof account.username === 'string' ? account.username : '',
    fullName: typeof account.fullName === 'string' ? account.fullName : '',
    businessCategory: typeof account.businessCategoryName === 'string' ? account.businessCategoryName : '',
    isBusiness: Boolean(account.isBusinessAccount),
    followersLabel: fmt(followers),
    followingLabel: fmt(following),
    postsLabel: fmt(postsCount),
    avgLikesLabel: fmt(avgLikes),
    avgCommentsLabel: fmt(avgComments),
    avgEngagementLabel: fmt(avgEngagement),
    topLikedLabel: topLiked ? `${topLiked.likeCount} likes` : '—',
    topCommentedLabel: topCommented ? `${topCommented.commentCount} comments` : '—',
  }
})

const previewThumbs = computed(() =>
  exportPosts.value
    .map((p) => postThumb(p))
    .filter((t): t is string => Boolean(t))
    .slice(0, 9),
)

const enrichedPosts = computed(() =>
  exportPosts.value.map((p) => ({
    ...p,
    _comments: commentsForPost(p),
    _thumb: postThumb(p),
  } as Record<string, unknown> & { _comments: unknown[]; _thumb: string | null })),
)

const modalMediaUrls = computed(() => {
  if (!selectedPost.value) return []
  const urls = selectedPost.value.mediaUrls
  if (Array.isArray(urls) && urls.length) return urls as string[]
  const t = postThumb(selectedPost.value)
  return t ? [t] : []
})

// ── Helpers ──

function shortCodeFromUrl(url: string): string {
  const m = url.match(/instagram\.com\/(?:p|reel)\/([^/?#]+)/)
  return m ? m[1] : ''
}

function commentsForPost(p: Record<string, unknown>): unknown[] {
  const ext = String(p.externalPostId || '')
  const perm = typeof p.permalink === 'string' ? p.permalink : ''
  const sc = shortCodeFromUrl(perm)
  const byPost = sessionExport.value?.instagram?.commentsByPost as
    | Array<{ postKey: string; comments: unknown[] }>
    | undefined
  if (!byPost?.length) return []
  const hit = byPost.find(
    (x) => x.postKey === ext || (sc && x.postKey === sc) || (perm && x.postKey && perm.includes(x.postKey)),
  )
  return hit?.comments ?? []
}

function postThumb(p: Record<string, unknown>): string | null {
  const a = p.cachedIconUrl || p.cachedThumbnailUrl || p.thumbnailUrl
  return typeof a === 'string' ? a : null
}

function liveThumb(p: Record<string, unknown>): string | null {
  const a = p.cachedIconUrl || p.cachedThumbnailUrl || p.thumbnailUrl
  return typeof a === 'string' ? a : null
}

function pretty(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2)
  } catch {
    return String(v)
  }
}


function sessionTabTitle(s: SessionRow): string {
  const d = new Date(s.startedAt)
  const t = d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  const st = s.finishedAt ? '' : ' …'
  return `${t}${st}`
}

function formatDate(v: unknown): string {
  if (!v || typeof v !== 'string') return '—'
  try {
    return new Date(v).toLocaleDateString(undefined, { dateStyle: 'medium' })
  } catch {
    return String(v)
  }
}

function openPostModal(p: Record<string, unknown>) {
  selectedPost.value = p
  postCarouselIndex.value = 0
  postModalOpen.value = true
}

function prevSlide() {
  if (postCarouselIndex.value > 0) postCarouselIndex.value--
}

function nextSlide() {
  if (postCarouselIndex.value < modalMediaUrls.value.length - 1) postCarouselIndex.value++
}

// ── Data loading ──

async function loadList() {
  loadingList.value = true
  try {
    const res = await apiClient.getCompanies({ limit: 100 })
    companies.value = res.companies as CompanyRow[]
  } catch (e) {
    console.error(e)
    message.error('Failed to load companies')
  } finally {
    loadingList.value = false
  }
}

async function loadSessionsAndExport() {
  if (!detailId.value) return
  try {
    const { sessions } = await apiClient.getResearchSessions(detailId.value)
    sessionsList.value = sessions
    if (sessions.length) {
      const preferred = activeSessionId.value && sessions.some((s) => s.id === activeSessionId.value)
      const sid = preferred ? activeSessionId.value! : sessions[0].id
      await loadSessionExport(sid)
    } else {
      activeSessionId.value = null
      sessionExport.value = null
      sessionRuns.value = []
    }
  } catch (e) {
    console.error(e)
  }
}

async function loadSessionExport(sessionId: string) {
  if (!detailId.value) return
  activeSessionId.value = sessionId
  try {
    const d = await apiClient.getResearchSession(detailId.value, sessionId)
    sessionExport.value = (d.session.export as Record<string, any>) || null
    sessionRuns.value = d.runs
  } catch (e) {
    console.error(e)
    message.error('Failed to load session export')
  }
}

function onSessionTabChange(name: string | number) {
  void loadSessionExport(String(name))
}

async function selectCompany(id: string) {
  if (detailId.value === id) return
  detailId.value = id
  activeSessionId.value = null
  previewPanelOpen.value = true
  await refreshDetail()
}

async function refreshDetail() {
  if (!detailId.value) return
  loadingDetail.value = true
  try {
    const res = await apiClient.getCompany(detailId.value, {
      includePosts: true,
      includeComments: true,
      postsLimit: 200,
    })
    detailCompany.value = res.company as Record<string, any>
    detailPosts.value = (res.posts ?? []) as Array<Record<string, unknown>>
    await loadSessionsAndExport()
    if (detailCompany.value?.researchInProgress) {
      startPoll()
    } else {
      stopPoll()
    }
  } catch (e) {
    console.error(e)
    message.error('Failed to load company')
  } finally {
    loadingDetail.value = false
  }
}

function startPoll() {
  stopPoll()
  pollTimer = setInterval(() => {
    void refreshDetail()
  }, 4000)
}

function stopPoll() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

function clearDetail() {
  stopPoll()
  detailCompany.value = null
  detailPosts.value = []
  sessionsList.value = []
  activeSessionId.value = null
  sessionExport.value = null
  sessionRuns.value = []
}

watch(detailId, (newId) => {
  if (!newId) clearDetail()
})

onMounted(() => {
  void loadList()
})

onUnmounted(() => {
  stopPoll()
})

// ── CRUD actions ──

async function submitCreate() {
  if (
    !createForm.value.name?.trim() ||
    !createForm.value.websiteUrl?.trim() ||
    !createForm.value.instagramHandle?.trim()
  ) {
    message.warning('Name, website, and Instagram handle are required')
    return
  }
  creating.value = true
  try {
    await apiClient.createCompany({
      name: createForm.value.name.trim(),
      websiteUrl: createForm.value.websiteUrl.trim(),
      instagramHandle: createForm.value.instagramHandle.trim(),
      relationType: createForm.value.relationType ?? undefined,
    })
    message.success('Company created')
    showCreate.value = false
    createForm.value = { name: '', websiteUrl: '', instagramHandle: '', relationType: null }
    await loadList()
  } catch (e) {
    console.error(e)
    message.error('Create failed')
  } finally {
    creating.value = false
  }
}

async function startRun() {
  if (!detailId.value) return
  startingRun.value = true
  try {
    const res = await apiClient.runCompanyResearch(detailId.value, { depth: runDepth.value })
    message.success('Research started (runs in background)')
    if (res.sessionId) {
      activeSessionId.value = res.sessionId
    }
    await refreshDetail()
    startPoll()
  } catch (e) {
    console.error(e)
    message.error('Could not start research')
  } finally {
    startingRun.value = false
  }
}

async function cancelRun() {
  if (!detailId.value) return
  if (!window.confirm('Cancel this research run? Current phase may finish first, then stop.')) return
  cancellingRun.value = true
  try {
    await apiClient.cancelCompanyResearch(detailId.value)
    message.success('Research cancel requested')
    await refreshDetail()
    await loadList()
  } catch (e) {
    console.error(e)
    message.error('Could not cancel research')
  } finally {
    cancellingRun.value = false
  }
}

async function deleteCompanyFromList(row: CompanyRow) {
  if (!window.confirm(`Delete company "${row.name}"? This cannot be undone.`)) return
  deletingCompanyId.value = row.id
  try {
    await apiClient.deleteCompany(row.id)
    message.success('Company deleted')
    if (detailId.value === row.id) {
      detailId.value = null
    }
    await loadList()
  } catch (e) {
    console.error(e)
    message.error('Failed to delete company')
  } finally {
    deletingCompanyId.value = null
  }
}

// ── Table column definitions ──

const runColumns: DataTableColumns<(typeof sessionRuns.value)[0]> = [
  { title: 'Source', key: 'source', width: 140 },
  { title: 'Status', key: 'status', width: 100 },
  { title: 'Apify run', key: 'apifyRunId', ellipsis: { tooltip: true } },
  {
    title: 'Error',
    key: 'error',
    ellipsis: { tooltip: true },
    render: (row) => row.error || '—',
  },
  {
    title: 'At',
    key: 'createdAt',
    width: 160,
    render: (row) => new Date(row.createdAt).toLocaleString(),
  },
]

const listModeColumns: DataTableColumns<CompanyRow> = [
  { title: 'Name', key: 'name', ellipsis: { tooltip: true } },
  { title: 'Website', key: 'websiteUrl', ellipsis: { tooltip: true }, width: 200 },
  { title: 'IG', key: 'instagramHandle', width: 140 },
  {
    title: 'Relation',
    key: 'relationType',
    width: 110,
    render: (row) => row.relationType || '—',
  },
  {
    title: 'Last research',
    key: 'lastResearchAt',
    width: 160,
    render: (row) => (row.lastResearchAt ? formatDate(row.lastResearchAt) : '—'),
  },
  {
    title: 'Status',
    key: 'researchInProgress',
    width: 80,
    render: (row) =>
      h('span', {
        style: `display:inline-block;width:10px;height:10px;border-radius:50%;background:${row.researchInProgress ? '#18a058' : '#888'};`,
        title: row.researchInProgress ? 'Running' : 'Idle',
      }),
  },
  {
    title: '',
    key: 'actions',
    width: 60,
    render: (row) =>
      h(
        NButton,
        {
          size: 'small',
          type: 'error',
          tertiary: true,
          loading: deletingCompanyId.value === row.id,
          title: 'Delete company',
          onClick: (e: MouseEvent) => {
            e.stopPropagation()
            void deleteCompanyFromList(row)
          },
        },
        { default: () => '🗑' },
      ),
  },
]
</script>

<style scoped>
.research-tab {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}
.research-tab.media-mode-active {
  max-width: none;
  padding: 16px 24px;
}
.research-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}
.research-header h1 {
  margin: 0;
  font-size: 1.5rem;
}
.sub {
  margin: 4px 0 0;
  color: var(--n-text-color-3);
  font-size: 0.9rem;
}
.err-pre,
.json-pre {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  max-height: 320px;
  overflow: auto;
}
.json-pre.sm {
  max-height: 160px;
}
.snippet {
  font-size: 12px;
  color: var(--n-text-color-3);
  margin-top: 4px;
}
.inner-tabs {
  margin-top: 8px;
}
.site-media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
}
.site-thumb {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--n-border-color);
}

/* ── List mode ── */
.list-toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
}
.list-mode-body {
  display: flex;
  gap: 0;
  align-items: flex-start;
  position: relative;
}
.list-main {
  flex: 1;
  min-width: 0;
  transition: margin-right 0.25s ease;
}
.list-main.with-panel {
  margin-right: 320px;
}
:deep(.row-selected td) {
  background: color-mix(in srgb, var(--n-primary-color) 12%, transparent) !important;
}
:deep(.n-data-table .n-data-table-tr) {
  cursor: pointer;
}

/* Preview panel */
.preview-panel {
  position: absolute;
  right: 0;
  top: 0;
  width: 304px;
  border: 1px solid var(--n-border-color);
  border-radius: 8px;
  padding: 12px;
  background: var(--n-card-color);
}
.preview-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.preview-company-name {
  font-weight: 600;
  font-size: 14px;
}
.preview-stats {
  display: flex;
  gap: 0;
  margin-bottom: 12px;
  border: 1px solid var(--n-border-color);
  border-radius: 6px;
  overflow: hidden;
}
.stat-item {
  flex: 1;
  text-align: center;
  padding: 8px 4px;
  border-right: 1px solid var(--n-border-color);
}
.stat-item:last-child {
  border-right: none;
}
.stat-val {
  font-weight: 700;
  font-size: 15px;
}
.stat-lbl {
  font-size: 10px;
  color: var(--n-text-color-3);
  margin-top: 2px;
}
.preview-thumb-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3px;
}
.preview-thumb {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 3px;
}
.preview-no-media {
  grid-column: 1 / -1;
  text-align: center;
  font-size: 12px;
  color: var(--n-text-color-3);
  padding: 12px;
}
.preview-loading {
  text-align: center;
  font-size: 12px;
  color: var(--n-text-color-3);
  padding: 24px;
}
.panel-reopen-btn {
  position: absolute;
  right: 0;
  top: 0;
}
.slide-right-enter-active,
.slide-right-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.slide-right-enter-from,
.slide-right-leave-to {
  opacity: 0;
  transform: translateX(12px);
}

/* ── Media mode ── */
.media-mode-layout {
  display: flex;
  height: calc(100vh - 150px);
  border: 1px solid var(--n-border-color);
  border-radius: 8px;
  overflow: hidden;
}
.media-sidebar {
  width: 260px;
  flex-shrink: 0;
  border-right: 1px solid var(--n-border-color);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.sidebar-search {
  padding: 8px;
  border-bottom: 1px solid var(--n-border-color);
  flex-shrink: 0;
}
.sidebar-list {
  flex: 1;
  overflow-y: auto;
}
.sidebar-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 12px;
  cursor: pointer;
  font-size: 13px;
  border-bottom: 1px solid var(--n-border-color);
  transition: background 0.1s;
}
.sidebar-item:hover {
  background: var(--n-color-hover);
}
.sidebar-item.active {
  background: color-mix(in srgb, var(--n-primary-color) 15%, transparent);
}
.sidebar-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}
.sidebar-relation {
  font-size: 10px;
  color: var(--n-text-color-3);
  background: var(--n-color-hover);
  padding: 1px 5px;
  border-radius: 3px;
  white-space: nowrap;
}
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot-running {
  background: #18a058;
}
.dot-idle {
  background: var(--n-border-color);
}
.media-main {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  min-width: 0;
}
.media-empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

/* Media mode company header */
.media-company-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--n-border-color);
  flex-wrap: wrap;
}
.media-company-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.media-company-name {
  font-weight: 700;
  font-size: 18px;
}
.media-company-link,
.media-company-ig {
  font-size: 13px;
  color: var(--n-text-color-3);
}
.media-company-link:hover {
  color: var(--n-primary-color);
}

/* Post card grid */
.post-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}
.post-media-card {
  border: 1px solid var(--n-border-color);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.15s, transform 0.15s;
}
.post-media-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}
.post-thumb-wrap {
  aspect-ratio: 1;
  overflow: hidden;
  background: var(--n-color-hover);
}
.post-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s;
}
.post-media-card:hover .post-thumb {
  transform: scale(1.03);
}
.post-thumb-placeholder {
  width: 100%;
  height: 100%;
  background: var(--n-color-hover);
}
.post-card-body {
  padding: 8px;
}
.post-caption-clip {
  font-size: 12px;
  color: var(--n-text-color-2);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0 0 6px;
  line-height: 1.4;
}
.post-card-meta {
  display: flex;
  gap: 8px;
  font-size: 11px;
  color: var(--n-text-color-3);
  align-items: center;
}
.post-date {
  margin-left: auto;
}

/* Post detail modal */
.post-modal-body {
  display: flex;
  gap: 20px;
  height: calc(88vh - 100px);
  overflow: hidden;
}
.post-modal-media {
  flex: 0 0 55%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #111;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}
.modal-media-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.carousel-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  height: calc(100% - 32px);
  justify-content: center;
}
.carousel-wrap img {
  flex: 1;
  max-width: calc(100% - 80px);
  max-height: 100%;
  object-fit: contain;
}
.carousel-dots {
  display: flex;
  gap: 6px;
  justify-content: center;
  padding: 8px;
  height: 32px;
  flex-shrink: 0;
}
.carousel-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.35);
  cursor: pointer;
  transition: background 0.15s;
}
.carousel-dot.active {
  background: #fff;
}
.modal-media-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #fff;
}
.post-modal-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  min-width: 0;
}
.post-modal-meta {
  display: flex;
  gap: 14px;
  align-items: center;
  font-size: 14px;
  flex-wrap: wrap;
  border-bottom: 1px solid var(--n-border-color);
  padding-bottom: 10px;
}
.post-modal-caption {
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  color: var(--n-text-color-2);
}
.post-modal-comments-heading {
  font-weight: 600;
  font-size: 13px;
}
.post-modal-comments {
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
}
</style>
